<template>
  <div class="security-guards">
    <h2 class="subheading grey--text">{{ $t('security_manage_guards') }}</h2>
    <v-container class="my-5">
      <v-layout align-center justify-center fill-height>
          <v-btn color="primary" dark @click="showCreateGuardDialog = true">
            + {{ $t('security_create_new_guard') }}
          </v-btn>
      </v-layout>

      <v-layout class="my-4" align-center justify-center fill-height>
        <v-flex>
          <v-card-title>
            {{ $t('security_guards_list') }}
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="search"
              :label="$t('Buscar')"
              single-line
              hide-details
              class="mr-3"
            ></v-text-field>
            <v-btn color="error" dark small @click="downloadPDF" :disabled="guards.length === 0">
              <v-icon left small>picture_as_pdf</v-icon>
              Generar PDF
            </v-btn>
          </v-card-title>

          <v-alert v-if="securityError" type="error" dismissible class="mb-3">
            {{ securityError }}
          </v-alert>

          <v-data-table
            :headers="guardHeaders"
            :items="guards"
            :search="search"
            :loading="isLoading"
            :rows-per-page-text="$t('filas_por_pagina')"
            :rows-per-page-items="[
              {text: '5', value: 5},
              {text: '10', value: 10},
              {text: '25', value: 25},
              {text: this.$t('todos'), value: -1}
            ]"
            class="elevation-1"
          >
            <template slot="items" slot-scope="props">
              <td class="justify-center layout px-0">
                <v-icon medium class="mr-2" color="primary" @click="viewGuardDetail(props.item)">visibility</v-icon>
                <v-icon medium class="mr-2" color="blue" @click="openEditDialog(props.item)">edit</v-icon>
                <v-icon medium class="mr-2" color="warning" @click="openAssignGuardDialog(props.item)">assignment_ind</v-icon>
                <v-icon medium class="mr-2" color="success" @click="downloadGuardGafete(props.item)" title="Descargar gafete">card_giftcard</v-icon>
                <v-icon medium color="red" @click="openDeleteDialog(props.item)">delete</v-icon>
              </td>
              <td>{{ props.item.first_name }} {{ props.item.last_name }}</td>
              <td>{{ props.item.username || 'N/A' }}</td>
              <td>{{ props.item.badge_number || 'N/A' }}</td>
              <td>{{ props.item.assigned_company_name || '-' }}</td>
              <td>{{ props.item.is_active ? $t('Si') : 'No' }}</td>
            </template>
            <template v-slot:no-results>
              <v-alert :value="true" color="error" icon="warning">
                {{$t('ningun_resultado_1')}} "{{ search }}" {{$t('ningun_resultado_2')}}
              </v-alert>
            </template>
            <template v-slot:pageText="props">
              {{props.rowsPerPage}}
              {{$t('security_guards_list')}} {{ props.pageStart }} - {{ props.pageStop }} {{$t('prep_de')}} {{ props.itemsLength }}
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- DIALOG: Crear/Editar Guardia -->
    <v-dialog v-model="showCreateGuardDialog" max-width="500px" persistent>
      <v-form ref="createGuardForm" @submit.prevent="isEditing ? saveEditGuard() : createGuard()">
        <v-card class="elevation-5">
          <v-toolbar dark color="primary">
            <v-toolbar-title>{{ isEditing ? $t('modificar') : $t('security_create_new_guard') }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon color="white" flat @click="closeCreateDialog" class="align-self-end">
              <v-icon>close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-tabs v-model="createGuardTab" grow>
            <v-tab>{{ $t('general_information') || 'Informacion general' }}</v-tab>
            <v-tab>{{ $t('documentation') || 'Documentacion' }}</v-tab>

            <v-tab-item>
              <v-card-text class="pt-4">
                 <v-layout row wrap justify-center>
                   <v-flex xs12 class="text-xs-center mb-4">
                     <v-avatar size="100" color="grey lighten-2" class="elevation-2" style="cursor: pointer;" @click="openAvatarModal()">
                       <img v-if="newGuard.avatarPreview" :src="newGuard.avatarPreview" alt="Avatar">
                       <v-icon v-else size="64" color="white">person</v-icon>
                     </v-avatar>
                     <div class="mt-3">
                       <v-btn small color="primary" @click="openAvatarModal()" outline round>
                         <v-icon left>camera_alt</v-icon> {{$t('subir_imagen')}}
                       </v-btn>
                     </div>
                     <input type="file" ref="avatarInput" style="display: none;" accept="image/jpeg, image/png" @change="onAvatarFilePicked" />
                   </v-flex>

                   <v-flex xs12 sm6 class="px-2">
                     <v-text-field
                       v-model="newGuard.firstName"
                       :label="$t('Nombre')"
                       prepend-icon="person"
                       required
                     ></v-text-field>
                   </v-flex>

                   <v-flex xs12 sm6 class="px-2">
                     <v-text-field
                       v-model="newGuard.lastName"
                       :label="$t('Apellido')"
                       prepend-icon="person_outline"
                       required
                     ></v-text-field>
                   </v-flex>

                   <v-flex xs12 sm6 class="px-2 mt-2">
                     <v-text-field
                       v-model="newGuard.password"
                       :label="$t('password')"
                       prepend-icon="lock"
                       type="password"
                       :required="!isEditing"
                       :hint="isEditing ? 'Deja en blanco para no cambiar' : $t('password_min_length')"
                     ></v-text-field>
                   </v-flex>

                   <v-flex xs12 sm6 class="px-2 mt-2" v-if="isEditing">
                     <v-switch v-model="newGuard.isActive" :label="$t('Activo')"></v-switch>
                   </v-flex>
                 </v-layout>
              </v-card-text>
            </v-tab-item>

            <v-tab-item>
              <v-card-text class="pt-4">
                <v-layout row wrap>
                  <v-flex xs12 class="mb-3">
                    <v-text-field
                      :value="newGuard.ineFrontName"
                      label="Credenciales laborales"
                      prepend-icon="badge"
                      readonly
                      placeholder="Selecciona una imagen"
                    ></v-text-field>
                    <v-btn small color="primary" outline round @click="$refs.credentialsInput.click()">
                      <v-icon left>attach_file</v-icon>
                      Subir Credenciales laborales
                    </v-btn>
                    <input
                      type="file"
                      ref="credentialsInput"
                      style="display: none;"
                      accept="image/jpeg, image/png"
                      @change="onCredentialsFilePicked($event)"
                    />
                  </v-flex>
                  <v-flex xs12>
                    <v-text-field
                      :value="newGuard.responsibilityFormatName"
                      label="Formato de lineamientos ambientales (PDF)"
                      prepend-icon="picture_as_pdf"
                      readonly
                      placeholder="Selecciona un archivo PDF"
                    ></v-text-field>
                    <v-btn small color="primary" outline round @click="$refs.responsibilityFormatInput.click()">
                      <v-icon left>attach_file</v-icon>
                      Subir formato de lineamientos ambientales
                    </v-btn>
                    <input
                      type="file"
                      ref="responsibilityFormatInput"
                      style="display: none;"
                      accept="application/pdf"
                      @change="onDocumentFilePicked('responsibilityFormatFile', 'responsibilityFormatName', $event)"
                    />
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-tab-item>
          </v-tabs>

          <v-card-actions class="pb-4 px-4 justify-center">
            <v-btn color="grey darken-1" flat @click="closeCreateDialog">
               {{ $t('cancel') }}
            </v-btn>
            <v-btn 
               color="primary" 
               type="submit"
               :loading="isLoading"
               round
               class="px-4"
            >
               {{ isEditing ? $t('modificar') : $t('agregar') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- DIALOG: Seleccionar Avatar (Cámara o Archivo) -->
    <v-dialog v-model="showAvatarModal" max-width="500px" persistent>
      <v-card class="elevation-5">
        <v-toolbar dark color="primary">
          <v-toolbar-title>Foto de Perfil</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon color="white" flat @click="closeAvatarModal" class="align-self-end">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-layout row wrap justify-center>
            <v-flex xs12 class="text-xs-center">
              <p class="subheading grey--text">Selecciona cómo deseas obtener la foto</p>
            </v-flex>

            <!-- Opción 1: Usar cámara -->
            <v-flex xs12 sm6 class="pa-2">
              <v-card flat outlined class="pa-4 text-center" style="cursor: pointer; transition: all 0.3s;" @click="startWebcam" @mouseenter="hoverOption = 'camera'" @mouseleave="hoverOption = null" :style="{background: hoverOption === 'camera' ? '#f5f5f5' : 'transparent', border: hoverOption === 'camera' ? '2px solid #1976d2' : '1px solid #e0e0e0'}">
                <v-icon size="48" color="primary" class="mb-3">videocam</v-icon>
                <div class="subheading font-weight-bold">Usar Cámara</div>
                <p class="caption grey--text mt-2">Tomar foto con webcam</p>
              </v-card>
            </v-flex>

            <!-- Opción 2: Subir archivo -->
            <v-flex xs12 sm6 class="pa-2">
              <v-card flat outlined class="pa-4 text-center" style="cursor: pointer; transition: all 0.3s;" @click="$refs.avatarInput.click()" @mouseenter="hoverOption = 'upload'" @mouseleave="hoverOption = null" :style="{background: hoverOption === 'upload' ? '#f5f5f5' : 'transparent', border: hoverOption === 'upload' ? '2px solid #1976d2' : '1px solid #e0e0e0'}">
                <v-icon size="48" color="primary" class="mb-3">image</v-icon>
                <div class="subheading font-weight-bold">Subir Archivo</div>
                <p class="caption grey--text mt-2">Seleccionar imagen</p>
              </v-card>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions class="pb-4 px-4 justify-center">
          <v-btn color="grey darken-1" flat @click="closeAvatarModal">
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOG: Capturar foto con cámara -->
    <v-dialog v-model="showWebcamDialog" max-width="600px" persistent>
      <v-card class="elevation-5">
        <v-toolbar dark color="primary">
          <v-toolbar-title>Capturar Foto</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon color="white" flat @click="closeWebcamDialog" class="align-self-end">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-layout row wrap justify-center>
            <v-flex xs12 class="text-xs-center">
              <!-- Video de la cámara -->
              <div v-if="!webcamCapture && cameraActive" class="mb-4">
                <video
                  ref="videoElement"
                  autoplay
                  muted
                  playsinline
                  style="width: 100%; max-width: 500px; border-radius: 8px; background: #000;"
                ></video>
                <p class="caption grey--text mt-2">Posiciónate frente a la cámara</p>
              </div>

              <!-- Foto capturada -->
              <div v-else-if="webcamCapture" class="mb-4">
                <img :src="webcamCapture" alt="Captura" style="width: 100%; max-width: 500px; border-radius: 8px;">
              </div>

              <!-- Mensaje de error -->
              <v-alert v-if="webcamError" type="error" class="mb-4">
                {{ webcamError }}
              </v-alert>

              <!-- Estado de carga -->
              <div v-if="loadingCamera" class="mb-4">
                <v-progress-circular indeterminate color="primary" class="mb-2"></v-progress-circular>
                <p class="caption grey--text">Inicializando cámara...</p>
              </div>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions class="pb-4 px-4 justify-center">
          <v-btn color="grey darken-1" flat @click="closeWebcamDialog">
            {{ $t('cancel') }}
          </v-btn>

          <v-btn
            v-if="!webcamCapture && cameraActive"
            color="primary"
            @click="capturePhoto"
            :loading="loadingCamera"
          >
            <v-icon left>camera</v-icon> Capturar Foto
          </v-btn>

          <v-btn
            v-else-if="webcamCapture"
            color="primary"
            @click="resetCamera"
            class="mr-2"
          >
            <v-icon left>refresh</v-icon> Retomar
          </v-btn>

          <v-btn
            v-if="webcamCapture"
            color="success"
            @click="acceptWebcamPhoto"
          >
            <v-icon left>check</v-icon> Aceptar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOG: Asignar Guardia a Empresa A -->
    <v-dialog v-model="showAssignGuardDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title>{{ $t('security_assign_guard') }}</v-card-title>
        <v-card-text>
          <p class="mb-4">{{ selectedGuard ? selectedGuard.first_name + ' ' + selectedGuard.last_name : '' }}</p>

          <v-select
            v-model="selectedAssignClient"
            :items="assignedClients"
            item-text="name"
            item-value="id"
            :label="$t('security_select_client')"
            clearable
            outline
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" flat @click="showAssignGuardDialog = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn 
            color="primary" 
            flat 
            @click="assignGuard"
            :loading="isLoading"
          >
            {{ $t('assign') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOG: Detalle de Guardia -->
    <v-dialog v-model="showGuardDetailDialog" max-width="600px">
      <v-card class="elevation-5 rounded-lg">
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ $t('security_guard_details') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon color="white" flat @click="showGuardDetailDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text v-if="guardDetail" class="pt-4 pb-4">
          <v-layout row wrap justify-center>
            <v-flex xs12 class="text-xs-center mb-4">
              <v-avatar size="100" color="primary lighten-3" class="elevation-2">
                <img v-if="guardDetail.avatar" :src="guardDetail.avatar" alt="Avatar">
                <v-icon v-else size="64" color="white">person</v-icon>
              </v-avatar>
              <div class="title mt-3 font-weight-regular">{{ guardDetail.first_name }} {{ guardDetail.last_name }}</div>
              <v-chip outline :color="guardDetail.is_active ? 'success' : 'error'" class="mt-2 font-weight-bold" small>
                 <v-icon left size="16">{{ guardDetail.is_active ? 'check_circle' : 'error' }}</v-icon>
                 {{ guardDetail.is_active ? $t('active') : $t('inactive') }}
              </v-chip>
            </v-flex>
            
            <v-flex xs12 sm6 class="px-3 mt-3 text-xs-center">
              <div class="grey--text text--darken-1 caption text-uppercase">{{ $t('username') }}</div>
              <div class="subheading">{{ guardDetail.username }}</div>
            </v-flex>

            <v-flex xs12 sm6 class="px-3 mt-3 text-xs-center">
              <div class="grey--text text--darken-1 caption text-uppercase">{{ $t('badge_number') }}</div>
              <div class="subheading">{{ guardDetail.badge_number || 'N/A' }}</div>
            </v-flex>

            <v-flex xs12 sm6 class="px-3 mt-4 text-xs-center">
              <div class="grey--text text--darken-1 caption text-uppercase">{{ $t('security_assigned_to') }}</div>
              <div class="subheading">{{ guardDetail.assigned_company_name || '-' }}</div>
            </v-flex>

            <v-flex xs12 sm6 class="px-3 mt-4 text-xs-center">
              <div class="grey--text text--darken-1 caption text-uppercase">{{ $t('created_at') }}</div>
              <div class="subheading">{{ formatDate(guardDetail.created_at) }}</div>
            </v-flex>

            <!-- Archivos del guardia -->
            <v-flex xs12 class="px-3 mt-5">
              <v-divider class="mb-4"></v-divider>
              <div class="grey--text text--darken-2 caption text-uppercase font-weight-bold mb-3">
                <v-icon small class="mr-1">attach_file</v-icon> Documentos
              </div>
              <v-layout row wrap>
                <v-flex
                  v-for="doc in guardDocuments"
                  :key="doc.key"
                  xs12 sm6
                  class="pa-2"
                >
                  <v-card
                    flat
                    outlined
                    class="pa-3 d-flex align-center"
                    :style="{
                      'border': doc.url ? '1px solid #1976d2' : '1px solid #e0e0e0',
                      'border-radius': '8px',
                      'cursor': doc.url ? 'pointer' : 'default',
                      'transition': 'all 0.2s ease',
                      'background': doc.url ? '#f5f5f5' : 'transparent'
                    }"
                    @click="doc.url && openDocumentViewer(doc.label, doc.url)"
                    @mouseenter="hoverDoc = doc.key"
                    @mouseleave="hoverDoc = null"
                  >
                    <v-icon :color="doc.url ? 'primary' : 'grey lighten-1'" class="mr-3">
                      {{ doc.key === 'avatar' ? 'account_circle' : (doc.url ? 'visibility' : 'insert_drive_file') }}
                    </v-icon>
                    <div style="flex:1; min-width:0;">
                      <div class="caption grey--text" style="text-transform:uppercase; letter-spacing:.5px;">{{ doc.label }}</div>
                      <div v-if="doc.url" class="body-2 primary--text text-truncate">
                        {{ hoverDoc === doc.key ? 'Haz clic para ver' : 'Ver archivo' }}
                      </div>
                      <div v-else class="caption grey--text text--lighten-1">Sin archivo</div>
                    </div>
                  </v-card>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- DIALOG: Eliminar Guardia -->
    <v-dialog v-model="showDeleteGuardDialog" max-width="320">
      <v-card class="elevation-3 rounded-lg">
        <v-card-title class="headline">{{ $t('msg_eliminar_vigilante') || 'Eliminar Guardia' }}</v-card-title>
        <v-card-text>
          {{ $t('confirm_delete_guard') || '¿Estás seguro que deseas eliminar a este guardia?' }}
        </v-card-text>
        <v-card-actions class="px-3 pb-3">
          <v-btn color="grey darken-1" flat @click="showDeleteGuardDialog = false">No</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="confirmDeleteGuard" :loading="isLoading">{{ $t('Si') || 'Sí' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOG: Visualizar Documento -->
    <v-dialog v-model="showDocumentViewerDialog" max-width="700px">
      <v-card class="elevation-5">
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ documentViewerLabel }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon color="white" flat @click="showDocumentViewerDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <div v-if="documentViewerUrl" class="text-center">
            <!-- Visor para imágenes -->
            <img
              v-if="isImageDocument"
              :src="documentViewerUrl"
              alt="Documento"
              style="max-width: 100%; max-height: 60vh; object-fit: contain;"
            />
            <!-- Visor para PDFs -->
            <iframe
              v-else-if="isPdfDocument"
              :src="documentViewerUrl"
              style="width: 100%; height: 60vh; border: none; border-radius: 4px;"
            ></iframe>
            <!-- Fallback para otros tipos -->
            <div v-else class="py-5">
              <v-icon size="64" color="grey lighten-1" class="mb-3">insert_drive_file</v-icon>
              <p class="grey--text">No se puede previsualizar este tipo de archivo</p>
              <v-btn color="primary" :href="documentViewerUrl" target="_blank">
                <v-icon left>open_in_new</v-icon>
                Abrir en nueva pestaña
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import {
  FETCH_MY_COMPANY,
  FETCH_GUARDS,
  CREATE_GUARD,
  UPDATE_GUARD,
  FETCH_GUARD_DETAIL,
  ASSIGN_GUARD,
  DELETE_GUARD
} from '../../../store/modules/security'
import { mapGetters } from 'vuex'

export default {
  name: 'SecurityGuards',
  data() {
    return {
      showCreateGuardDialog: false,
      showAssignGuardDialog: false,
      showGuardDetailDialog: false,
      showDeleteGuardDialog: false,
      showDocumentViewerDialog: false,
      showAvatarModal: false,
      showWebcamDialog: false,
      documentViewerUrl: null,
      documentViewerLabel: '',
      hoverDoc: null,
      hoverOption: null,
      createGuardTab: 0,
      isEditing: false,
      editingGuardId: null,
      guardToDelete: null,
      cameraActive: false,
      loadingCamera: false,
      webcamCapture: null,
      webcamError: null,
      mediaStream: null,
      newGuard: {
        firstName: '',
        lastName: '',
        password: '',
        avatarPreview: '',
        avatarFile: null,
        isActive: true,
        ineFrontFile: null,
        ineFrontName: '',
        ineBackFile: null,
        ineBackName: '',
        responsibilityFormatFile: null,
        responsibilityFormatName: '',
        suaFile: null,
        suaName: ''
      },
      selectedGuard: null,
      selectedAssignClient: null,
      search: '',
      guardHeaders: []
    }
  },
  beforeUpdate(){
      this.guardHeaders = [
          { text: this.$t('acciones'), align: 'center', value: 'actions', sortable: false },
          { text: this.$t('Nombre'), align: 'left', value: 'first_name', sortable: true },
          { text: 'Usuario', align: 'left', value: 'username', sortable: true },
          { text: 'Placa', align: 'left', value: 'badge_number', sortable: true },
          { text: 'Asignado a', align: 'left', value: 'assigned_company_name', sortable: true },
          { text: this.$t('Activo'), align: 'left', value: 'is_active', sortable: true }
      ]
  },
  computed: {
    ...mapGetters(['getToken', 'getProfile']),
    ...mapGetters('security', [
      'isSecurityLoading',
      'getSecurityError',
      'getMyCompany',
      'getGuards',
      'getGuardDetail'
    ]),
    isLoading() {
      return this.isSecurityLoading
    },
    securityError() {
      return this.getSecurityError
    },
    guards() {
      return this.getGuards
    },
    guardDetail() {
      return this.getGuardDetail
    },
    guardDocuments() {
      if (!this.guardDetail) return [];
      const files = this.guardDetail.files || {};
      return [
        { key: 'avatar',              label: 'Foto / Avatar',                      url: files.avatar              || this.guardDetail.avatar              || null },
        { key: 'ine_frente',          label: 'Credenciales laborales',             url: files.ine_frente          || this.guardDetail.ine_frente          || null },
        { key: 'responsibility_format', label: 'Formato de lineamientos ambientales', url: files.responsibility_format || this.guardDetail.responsibility_format || null },
      ];
    },
    assignedClients() {
      if (this.getMyCompany && this.getMyCompany.company_contracts) {
        // Normalize contract response because backend payloads can vary by version.
        return this.getMyCompany.company_contracts
          .filter(contract => {
            if (!contract.status) return true
            return String(contract.status).toUpperCase() === 'ACTIVE'
          })
          .map(contract => ({
            id: this.getClientIdFromContract(contract),
            name: contract.company_name || contract.host_name || contract.client_company_name || contract.name || 'N/A',
            status: contract.status,
            status_display: contract.status_display,
            created_at: contract.created_at
          }))
          .filter(client => !!client.id)
      }
      if (this.getMyCompany && this.getMyCompany.contracts) {
        // Fallback for old API response format
        return this.getMyCompany.contracts
          .filter(contract => {
            if (!contract.status) return true
            return String(contract.status).toUpperCase() === 'ACTIVE'
          })
          .map(contract => ({
            id: this.getClientIdFromContract(contract),
            name: contract.company_name || contract.host_name || contract.client_company_name || contract.name || 'N/A',
            status: contract.status,
            status_display: contract.status_display,
            created_at: contract.created_at
          }))
          .filter(client => !!client.id)
      }
      if (this.getMyCompany && this.getMyCompany.client_companies) {
        // Fallback for old API response format
        return this.getMyCompany.client_companies
          .map(client => ({
            ...client,
            id: client.id || client.company_id || client.client_company_id
          }))
          .filter(client => !!client.id)
      }
      return []
    },
    isImageDocument() {
      if (!this.documentViewerUrl) return false
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(this.documentViewerUrl)
    },
    isPdfDocument() {
      if (!this.documentViewerUrl) return false
      return /\.pdf$/i.test(this.documentViewerUrl)
    }
  },
  methods: {
    getClientIdFromContract(contract) {
      if (!contract) return null
      return contract.company_id || contract.client_company_id || contract.host_company_id || contract.id_company || contract.id || null
    },
    getAssignedCompanyIdFromGuard(guard) {
      if (!guard) return null
      if (guard.assigned_company_id) return guard.assigned_company_id
      if (guard.assigned_company && typeof guard.assigned_company === 'object') {
        return guard.assigned_company.id || guard.assigned_company.company_id || null
      }
      return guard.assigned_company || null
    },
    fetchData() {
      const token = this.getToken
      this.$store.dispatch(`security/${FETCH_MY_COMPANY}`, token)
      this.$store.dispatch(`security/${FETCH_GUARDS}`, token)
    },
    createGuard() {
      if (!this.newGuard.ineFrontFile) {
        this.$store.commit('snackbar_change_message', 'Credenciales laborales son obligatorias')
        this.$store.commit('snackbar_change_color', 'warning')
        this.$store.commit('snackbar_activate_true')
        this.createGuardTab = 1
        return
      }

      const token = this.getToken
      this.$store.dispatch(`security/${CREATE_GUARD}`, {
        token,
        firstName: this.newGuard.firstName,
        lastName: this.newGuard.lastName,
        password: this.newGuard.password,
        avatar: this.newGuard.avatarFile,
        ine: this.newGuard.ineFrontFile,
        ineReverso: this.newGuard.ineBackFile,
        responsibilityFormat: this.newGuard.responsibilityFormatFile,
        sua: this.newGuard.suaFile
      })
      .then(resp => {
        // Persist plaintext password in the guard object so it can appear in the PDF report
        if (resp && resp.id) {
          this._guardPasswords = this._guardPasswords || {}
          this._guardPasswords[resp.id] = this.newGuard.password
        }
        this.$store.commit('snackbar_change_message', this.$t('security_guard_created_success'))
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.showCreateGuardDialog = false
        this.newGuard = {
          firstName: '',
          lastName: '',
          password: '',
          avatarPreview: '',
          avatarFile: null,
          isActive: true,
          ineFrontFile: null,
          ineFrontName: '',
          ineBackFile: null,
          ineBackName: '',
          responsibilityFormatFile: null,
          responsibilityFormatName: '',
          suaFile: null,
          suaName: ''
        }
        this.createGuardTab = 0
        this.fetchData()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', this.$t('error_creating_guard'))
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
    },
    openEditDialog(guard) {
      this.isEditing = true;
      this.editingGuardId = guard.id;
      this.newGuard = {
        firstName: guard.first_name,
        lastName: guard.last_name,
        password: '',
        avatarPreview: guard.avatar || '',
        avatarFile: null,
        isActive: guard.is_active,
        ineFrontFile: null,
        ineFrontName: '',
        ineBackFile: null,
        ineBackName: '',
        responsibilityFormatFile: null,
        responsibilityFormatName: '',
        suaFile: null,
        suaName: ''
      };
      this.createGuardTab = 0;
      this.showCreateGuardDialog = true;
    },
    saveEditGuard() {
      const token = this.getToken;
      this.$store.dispatch(`security/${UPDATE_GUARD}`, {
        token,
        guardId: this.editingGuardId,
        firstName: this.newGuard.firstName,
        lastName: this.newGuard.lastName,
        password: this.newGuard.password || undefined,
        avatar: this.newGuard.avatarFile || undefined,
        isActive: this.newGuard.isActive,
        ine: this.newGuard.ineFrontFile || undefined,
        sua: this.newGuard.suaFile || undefined,
        responsibilityFormat: this.newGuard.responsibilityFormatFile || undefined
      })
      .then(() => {
        this.$store.commit('snackbar_change_message', this.$t('security_guard_updated_success') || 'Guardia actualizado');
        this.$store.commit('snackbar_change_color', 'success');
        this.$store.commit('snackbar_activate_true');
        this.closeCreateDialog();
        this.fetchData();
      })
      .catch((err) => {
        this.$store.commit('snackbar_change_message', this.$t('error_updating_guard') || 'Error al actualizar guardia');
        this.$store.commit('snackbar_change_color', 'error');
        this.$store.commit('snackbar_activate_true');
      });
    },
    closeCreateDialog() {
      this.showCreateGuardDialog = false;
      this.createGuardTab = 0;
      this.isEditing = false;
      this.editingGuardId = null;
      this.newGuard = {
        firstName: '',
        lastName: '',
        password: '',
        avatarPreview: '',
        avatarFile: null,
        isActive: true,
        ineFrontFile: null,
        ineFrontName: '',
        ineBackFile: null,
        ineBackName: '',
        responsibilityFormatFile: null,
        responsibilityFormatName: '',
        suaFile: null,
        suaName: ''
      };
      if (this.$refs.createGuardForm) {
        this.$refs.createGuardForm.reset();
      }
    },
    openAvatarModal() {
      this.showAvatarModal = true;
      this.webcamCapture = null;
      this.webcamError = null;
      this.cameraActive = false;
    },
    closeAvatarModal() {
      this.showAvatarModal = false;
      this.closeWebcamDialog();
    },
    startWebcam() {
      this.showAvatarModal = false;
      this.showWebcamDialog = true;
      this.loadingCamera = true;
      this.webcamError = null;
      this.webcamCapture = null;

      navigator.mediaDevices
        .getUserMedia({ video: { facingMode: 'user' } })
        .then(stream => {
          this.mediaStream = stream;
          // cameraActive must flip before we look for the ref: the <video>
          // tag is behind v-if="cameraActive" and doesn't exist until then.
          this.cameraActive = true;
          this.$nextTick(() => {
            const video = this.$refs.videoElement;
            if (video) {
              video.srcObject = stream;
              video.onloadedmetadata = () => {
                video.play().catch(err => {
                  console.error('Error playing video:', err);
                });
                this.loadingCamera = false;
              };
            } else {
              this.webcamError = 'No se pudo inicializar el elemento de video';
              this.cameraActive = false;
              this.loadingCamera = false;
            }
          });
        })
        .catch(err => {
          this.webcamError = 'No se pudo acceder a la cámara. Verifica los permisos del navegador.';
          this.loadingCamera = false;
          console.error('Camera error:', err);
        });
    },
    capturePhoto() {
      const video = this.$refs.videoElement;
      if (!video) return;

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      this.webcamCapture = canvas.toDataURL('image/jpeg', 0.95);
    },
    resetCamera() {
      this.webcamCapture = null;
    },
    acceptWebcamPhoto() {
      if (this.webcamCapture) {
        const canvas = document.createElement('canvas');
        const img = new Image();
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(blob => {
            const file = new File([blob], 'avatar_webcam.jpg', { type: 'image/jpeg' });
            this.newGuard.avatarFile = file;
            this.newGuard.avatarPreview = this.webcamCapture;
            this.closeWebcamDialog();
          });
        };
        img.src = this.webcamCapture;
      }
    },
    closeWebcamDialog() {
      this.showWebcamDialog = false;
      this.cameraActive = false;
      this.webcamCapture = null;
      this.webcamError = null;

      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => {
          track.stop();
        });
        this.mediaStream = null;
      }
    },
    onAvatarFilePicked(event) {
      const files = event.target.files;
      if (files && files[0]) {
        const file = files[0];
        if (file.type.indexOf('image/') !== 0) return;
        this.newGuard.avatarFile = file;
        const reader = new FileReader();
        reader.onload = e => {
          this.newGuard.avatarPreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    onDocumentFilePicked(fileKey, nameKey, event) {
      const files = event.target.files
      if (!files || !files[0]) return
      const file = files[0]
      this.newGuard[fileKey] = file
      this.newGuard[nameKey] = file.name
    },
    onCredentialsFilePicked(event) {
      const files = event.target.files
      if (!files || !files[0]) return
      const file = files[0]
      this.newGuard.ineFrontFile = file
      this.newGuard.ineBackFile = file
      this.newGuard.ineFrontName = file.name
      this.newGuard.ineBackName = file.name
    },
    viewGuardDetail(guard) {
      const token = this.getToken
      this.$store.dispatch(`security/${FETCH_GUARD_DETAIL}`, {
        token,
        guardId: guard.id
      })
      .then(() => {
        this.showGuardDetailDialog = true
      })
    },
    openAssignGuardDialog(guard) {
      this.selectedGuard = guard
      this.selectedAssignClient = this.getAssignedCompanyIdFromGuard(guard)
      this.showAssignGuardDialog = true
    },
    assignGuard() {
      if (!this.selectedGuard || !this.selectedGuard.id) {
        return
      }

      let assignedCompanyId = this.selectedAssignClient
      if (assignedCompanyId === '' || assignedCompanyId === undefined) {
        assignedCompanyId = null
      }

      if (assignedCompanyId !== null && assignedCompanyId !== undefined) {
        const normalizedCompanyId = Number(assignedCompanyId)
        if (!normalizedCompanyId) {
          this.$store.commit('snackbar_change_message', this.$t('security_select_client_required'))
          this.$store.commit('snackbar_change_color', 'warning')
          this.$store.commit('snackbar_activate_true')
          return
        }
        assignedCompanyId = normalizedCompanyId
      }

      const token = this.getToken
      this.$store.dispatch(`security/${ASSIGN_GUARD}`, {
        token,
        guardId: this.selectedGuard.id,
        assignedCompanyId
      })
      .then(resp => {
        this.$store.commit(
          'snackbar_change_message',
          assignedCompanyId === null
            ? 'Guardia desvinculado correctamente'
            : this.$t('security_guard_assigned_success')
        )
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.showAssignGuardDialog = false
        this.fetchData()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', this.$t('error_assigning_guard'))
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
    },
    openDeleteDialog(guard) {
      this.guardToDelete = guard;
      this.showDeleteGuardDialog = true;
    },
    confirmDeleteGuard() {
      if (!this.guardToDelete) return;

      const token = this.getToken;
      this.$store.dispatch(`security/${DELETE_GUARD}`, {
        token,
        guardId: this.guardToDelete.id
      })
      .then(resp => {
        this.$store.commit('snackbar_change_message', this.$t('security_guard_deleted_success'));
        this.$store.commit('snackbar_change_color', 'success');
        this.$store.commit('snackbar_activate_true');
        this.showDeleteGuardDialog = false;
        this.guardToDelete = null;
        this.fetchData();
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', this.$t('error_deleting_guard'));
        this.$store.commit('snackbar_change_color', 'error');
        this.$store.commit('snackbar_activate_true');
        this.showDeleteGuardDialog = false;
      })
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString(this.$i18n.locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    downloadPDF() {
      const doc = new jsPDF({ orientation: 'landscape' })
      const companyName = (this.getMyCompany && this.getMyCompany.company_name) || 'Empresa de Seguridad'
      const now = new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })

      // Header
      doc.setFontSize(16)
      doc.setTextColor(33, 33, 33)
      doc.text('Reporte de Guardias de Seguridad', 14, 18)
      doc.setFontSize(10)
      doc.setTextColor(100)
      doc.text(`Empresa: ${companyName}`, 14, 26)
      doc.text(`Generado el: ${now}`, 14, 32)

      const rows = this.guards.map((g, i) => [
        i + 1,
        `${g.first_name || ''} ${g.last_name || ''}`.trim() || 'N/A',
        g.username || 'N/A',
        g.badge_number || 'N/A',
        g.assigned_company_name || 'Sin asignar',
        g.is_active ? 'Activo' : 'Inactivo',
        g._plainPassword || (this._guardPasswords && this._guardPasswords[g.id]) || '(ver al crear)'
      ])

      doc.autoTable({
        startY: 38,
        head: [['#', 'Nombre', 'Usuario', 'Placa / Gafete', 'Empresa Asignada', 'Estado', 'Contraseña']],
        body: rows,
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [235, 245, 255] },
        styles: { fontSize: 9, cellPadding: 4 },
        columnStyles: {
          0: { halign: 'center', cellWidth: 10 },
          5: { halign: 'center', cellWidth: 20 },
          6: { halign: 'center', cellWidth: 30 }
        }
      })

      // Footer note about passwords
      const finalY = doc.lastAutoTable.finalY + 8
      doc.setFontSize(8)
      doc.setTextColor(150)
      doc.text(
        'Nota: Las contraseñas no se almacenan en texto plano. El campo muestra "(ver al crear)" para guardias existentes.',
        14,
        finalY
      )

      doc.save(`guardias_seguridad_${Date.now()}.pdf`)
    },
    async downloadGuardGafete(guard) {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' })
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()

      // Fondo blanco
      doc.setFillColor(255, 255, 255)
      doc.rect(0, 0, pageWidth, pageHeight, 'F')

      const avatarData = guard.avatar ? await this.loadImageAsDataUrl(guard.avatar) : null
      this.buildGafete(doc, guard, pageWidth, pageHeight, avatarData)
    },
    // Descarga la imagen como blob y la convierte a data URL; evita que el canvas quede "tainted" por CORS al usar Image()
    loadImageAsDataUrl(url) {
      return fetch(url)
        .then(resp => {
          if (!resp.ok) throw new Error('No se pudo descargar la imagen')
          return resp.blob()
        })
        .then(blob => new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        }))
        .catch(() => null)
    },
    // Dibuja la tarjeta tipo credencial (encabezado/pie con el color primario de la app) con foto, datos y QR
    buildGafete(doc, guard, pageWidth, pageHeight, avatarData) {
      const primary = [0, 43, 72] // #002b48, color primario de la app
      const cardWidth = 110
      const cardHeight = 178
      const cardX = (pageWidth - cardWidth) / 2
      const cardY = (pageHeight - cardHeight) / 2

      // Marco de la tarjeta
      doc.setFillColor(255, 255, 255)
      doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 6, 6, 'F')
      doc.setDrawColor(...primary)
      doc.setLineWidth(0.7)
      doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 6, 6, 'S')

      // Encabezado
      const headerHeight = 20
      doc.setFillColor(...primary)
      doc.roundedRect(cardX, cardY, cardWidth, headerHeight, 6, 6, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(13)
      doc.text('GAFETE DE SEGURIDAD', cardX + cardWidth / 2, cardY + 12, { align: 'center' })

      // Foto (separada del QR, en su propio marco)
      const photoSize = 45
      const photoX = cardX + (cardWidth - photoSize) / 2
      const photoY = cardY + headerHeight + 10
      if (avatarData) {
        const format = avatarData.substring(avatarData.indexOf('/') + 1, avatarData.indexOf(';')).toUpperCase()
        doc.addImage(avatarData, format === 'JPG' ? 'JPEG' : format, photoX, photoY, photoSize, photoSize)
      } else {
        doc.setFillColor(235, 238, 241)
        doc.rect(photoX, photoY, photoSize, photoSize, 'F')
        doc.setTextColor(150)
        doc.setFontSize(10)
        doc.text('Sin foto', photoX + photoSize / 2, photoY + photoSize / 2, { align: 'center' })
      }
      doc.setDrawColor(...primary)
      doc.setLineWidth(0.8)
      doc.rect(photoX, photoY, photoSize, photoSize, 'S')

      // Nombre y placa
      let cursorY = photoY + photoSize + 11
      doc.setTextColor(33, 33, 33)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(15)
      const fullName = `${guard.first_name} ${guard.last_name}`
      doc.text(fullName, cardX + cardWidth / 2, cursorY, { align: 'center' })

      cursorY += 7
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(11)
      doc.setTextColor(90, 90, 90)
      doc.text(`Placa: ${guard.badge_number || 'N/A'}`, cardX + cardWidth / 2, cursorY, { align: 'center' })

      // Divisor entre los datos y el QR
      cursorY += 6
      doc.setDrawColor(...primary)
      doc.setLineWidth(0.3)
      doc.line(cardX + 12, cursorY, cardX + cardWidth - 12, cursorY)

      const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(guard.unique_code)}&ecc=M&margin=0&size=800x800`
      const qrImg = new Image()
      qrImg.crossOrigin = 'anonymous'
      qrImg.src = qrApiUrl
      qrImg.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = qrImg.width
        canvas.height = qrImg.height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(qrImg, 0, 0)
        const qrData = canvas.toDataURL('image/png')

        const qrSize = 48
        const qrX = cardX + (cardWidth - qrSize) / 2
        const qrY = cursorY + 9
        doc.setDrawColor(...primary)
        doc.setLineWidth(0.6)
        doc.rect(qrX - 3, qrY - 3, qrSize + 6, qrSize + 6, 'S')
        doc.addImage(qrData, 'PNG', qrX, qrY, qrSize, qrSize)

        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.setTextColor(120, 120, 120)
        doc.text(`${guard.unique_code}`, cardX + cardWidth / 2, qrY + qrSize + 9, { align: 'center' })

        // Pie de página
        const footerHeight = 11
        const footerY = cardY + cardHeight - footerHeight
        doc.setFillColor(...primary)
        doc.roundedRect(cardX, footerY, cardWidth, footerHeight, 6, 6, 'F')
        doc.setTextColor(255, 255, 255)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(9)
        doc.text('Válido mientras esté activo', cardX + cardWidth / 2, footerY + 7, { align: 'center' })

        doc.save(`gafete_${guard.username}_${Date.now()}.pdf`)
      }
      qrImg.onerror = () => {
        doc.save(`gafete_${guard.username}_${Date.now()}.pdf`)
      }
    },
    openDocumentViewer(label, url) {
      if (!url) return
      this.documentViewerLabel = label
      this.documentViewerUrl = url
      this.showDocumentViewerDialog = true
    }
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push('/')
      return
    }
    if (this.getProfile.roll !== 8) {
      this.$router.push('/')
      return
    }
    this.fetchData()
  }
}
</script>

<style scoped>
.mb-3 {
  margin-bottom: 20px;
}
</style>
